import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card';
import FormField from '../../components/common/FormField';
import Button from '../../components/common/Button';
import { useQuickEventApi } from '../../hooks/useQuickEventApi';
import { QuickEventDTO } from '../../services/apiClient';
import { showAlert } from '../../utils/sweetAlertUtils';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';

const QuickEventView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getById, submitActivity, isLoading } = useQuickEventApi();
  
  const [quickEvent, setQuickEvent] = useState<QuickEventDTO | null>(null);
  const [formData, setFormData] = useState({
    categoryId: '',
    memberId: '',
    eventDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationRules: ValidationRules = {
    categoryId: [{ required: true, message: 'Category is required' }],
    memberId: [{ required: true, message: 'Member ID Number is required' }],
    eventDate: [{ required: true, message: 'Event Date is required' }],
  };

  const { errors, validate } = useFormValidation(validationRules);

  useEffect(() => {
    const fetchQuickEvent = async () => {
      if (id) {
        try {
          const response = await getById(id);
          if (response?.data) {
            setQuickEvent(response.data);
          }
        } catch (error) {
          console.error('Error fetching quick event:', error);
        }
      }
    };

    fetchQuickEvent();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate(formData)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await submitActivity({
        quickEventId: id!,
        idPassportNumber: formData.memberId,
        activityCategoryId: formData.categoryId,
        activityDate: new Date(formData.eventDate),
      });

      await showAlert({
        title: 'Success',
        text: 'Activity created successfully!',
        icon: 'success',
      });

      // Reset form
      setFormData({
        categoryId: '',
        memberId: '',
        eventDate: new Date().toISOString().split('T')[0],
      });

    } catch (error: any) {
      console.error('Error submitting quick event activity:', error);
      // The Axios instance uses transformResponse:[] so response bodies are raw strings.
      // The generated ApiException stores that raw string in error.response, so we must
      // JSON.parse it before accessing the server's message field.
      let parsedResponse: any = null;
      try {
        parsedResponse = typeof error?.response === 'string'
          ? JSON.parse(error.response)
          : error?.response;
      } catch { /* ignore parse errors */ }
      const message =
        parsedResponse?.message ||
        error?.message ||
        'Failed to submit activity. Please try again.';
      await showAlert({
        title: 'Error',
        text: message,
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <Card
        title={quickEvent?.title || 'Submit Activity'}
        subtitle={quickEvent ? `${quickEvent.location || ''} ${quickEvent.town || ''}`.trim() : ''}
      >
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <FormField
              id="categoryId"
              label="Category"
              type="dropdown"
              value={formData.categoryId}
              onChange={handleChange}
              placeholder="Select Category"
              required={true}
              error={errors.categoryId}
              options={
                quickEvent?.activityCategories?.map((cat) => ({
                  value: cat.id || '',
                  label: cat.title || '',
                })) || []
              }
            />

            <FormField
              id="memberId"
              label="Member ID Number"
              value={formData.memberId}
              onChange={handleChange}
              placeholder="Enter Member ID"
              required={true}
              error={errors.memberId}
            />

            <div className="mt-6">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        )}
      </Card>
      </div>
    </div>
  );
};

export default QuickEventView;